import { useId, useState } from 'react'
import { format, isValid, parseISO, startOfDay } from 'date-fns'
import { fr } from 'date-fns/locale'
import { CalendarDays } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Calendar } from '@/shared/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover'
import { cn } from '@/shared/lib/utils'

function toDate(iso: string | undefined): Date | undefined {
  if (!iso) return undefined
  const d = parseISO(iso)
  return isValid(d) ? startOfDay(d) : undefined
}

export interface FrenchDateInputProps {
  id?: string
  /** ISO `yyyy-MM-dd` or empty string. */
  value: string
  onChange: (iso: string) => void
  min?: string
  max?: string
  disabled?: boolean
  className?: string
  'aria-label'?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

/** Date picker with fixed French display (`jj/mm/aaaa`) - avoids OS-native `mm/dd/yyyy` inputs. */
export function FrenchDateInput({
  id,
  value,
  onChange,
  min,
  max,
  disabled,
  className,
  ...aria
}: FrenchDateInputProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const fallbackId = useId()
  const inputId = id ?? fallbackId

  const selected = toDate(value)
  const minDate = toDate(min)
  const maxDate = toDate(max)
  const display = selected ? format(selected, 'dd/MM/yyyy', { locale: fr }) : ''

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={inputId}
        type="button"
        disabled={disabled}
        className={cn(
          'flex h-12 w-full min-w-[10.5rem] items-center gap-2 rounded-[10px] border border-input bg-card px-3 text-left text-sm text-foreground transition-colors aria-invalid:border-destructive',
          'outline-none hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
          !display && 'text-muted-foreground',
          className,
        )}
        {...aria}
      >
        <CalendarDays className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="truncate">{display || t('shared.date_input.placeholder')}</span>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-2">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(day) => {
            if (!day) return
            onChange(format(day, 'yyyy-MM-dd'))
            setOpen(false)
          }}
          disabled={
            minDate && maxDate
              ? [{ before: minDate }, { after: maxDate }]
              : minDate
                ? { before: minDate }
                : maxDate
                  ? { after: maxDate }
                  : undefined
          }
        />
      </PopoverContent>
    </Popover>
  )
}
